import { useParams, Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { ArrowLeft, ArrowRight, ExternalLink, Target, Zap, TrendingUp, Check, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { getProjectBySlug, allProjects } from '../data/projects';
import { getGalleryBySlug } from '../data/projectGalleries';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

function AnimCounter({ target, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => `${Math.round(v)}${suffix}`);
  useEffect(() => { if (inView) animate(count, target, { duration: 2, ease: [0.22, 1, 0.36, 1] }); }, [inView, count, target]);
  return <motion.span ref={ref}>{rounded}</motion.span>;
}

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);
  const galleryImages = getGalleryBySlug(slug);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#070707] pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Proje Bulunamadı</h1>
          <Link to="/portfolyo" className="text-violet-400 hover:underline">Portföye Dön</Link>
        </div>
      </div>
    );
  }

  const currentIndex = allProjects.findIndex(p => p.slug === slug);
  const sameCategory = allProjects.filter(p => p.slug !== slug && p.category === project.category);
  const otherCategory = allProjects.filter(p => p.slug !== slug && p.category !== project.category);
  const relatedProjects = [...sameCategory, ...otherCategory].slice(0, 8);

  const metricNum = parseInt(project.metric.replace(/[^0-9]/g, '')) || 0;
  const metricSign = project.metric.includes('+') ? '+' : '';
  const metricSuffix = project.metric.includes('%') ? '%' : '';

  return (
    <div className="min-h-screen bg-[#070707] text-white" data-testid={`project-page-${slug}`}>

      {/* ═══ 1. CINEMATIC HERO ═══ */}
      <section className="relative h-[85vh] min-h-[600px] overflow-hidden" data-testid="project-hero">
        {/* bg image — galeri varsa yüksek çözünürlüklü ilk görseli kullan */}
        <div className="absolute inset-0">
          <img
            src={galleryImages.length > 0 ? galleryImages[0] : project.image}
            alt={project.title}
            className="w-full h-full object-cover"
            style={{ imageRendering: 'auto' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/70 to-[#070707]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070707]/80 to-transparent" />
          {/* Galerisi olmayan projeler için ekstra blur */}
          {galleryImages.length === 0 && (
            <div className="absolute inset-0 backdrop-blur-sm" />
          )}
        </div>

        {/* content */}
        <div className="relative z-10 h-full flex flex-col justify-end max-w-7xl mx-auto px-6 lg:px-12 pb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/portfolyo" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 text-sm">
              <ArrowLeft className="w-4 h-4" /> Portföye Dön
            </Link>

            <span className="inline-block px-4 py-1.5 mb-5 text-xs font-medium text-violet-300 bg-violet-500/10 rounded-full border border-violet-500/20 tracking-wider uppercase">
              {project.categoryLabel}
            </span>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-4 leading-[1.05] tracking-tight">
              {project.title}
            </h1>

            <p className="text-xl text-zinc-300 max-w-2xl mb-8 leading-relaxed">
              {project.description}
            </p>

            <div className="flex flex-wrap items-center gap-8">
              {/* floating metric card */}
              <div className="px-8 py-5 rounded-2xl bg-zinc-900/60 backdrop-blur-md border border-zinc-800/60">
                <div className="font-bold text-4xl bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
                  <AnimCounter target={metricNum} suffix={metricSuffix} />
                </div>
                <p className="text-zinc-400 text-sm mt-1">{project.metricLabel}</p>
              </div>

              <div className="h-12 w-px bg-zinc-700/50 hidden sm:block" />

              <div>
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Yıl</p>
                <p className="text-white font-semibold text-xl">{project.year}</p>
              </div>

              <div className="h-12 w-px bg-zinc-700/50 hidden sm:block" />

              <div>
                <p className="text-zinc-500 text-xs uppercase tracking-wider">Müşteri</p>
                <p className="text-white font-semibold text-xl">{project.client}</p>
              </div>

              <a href={project.behanceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-zinc-700/50 text-zinc-300 hover:text-white hover:border-zinc-500 transition-colors text-sm font-medium">
                Behance <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 2. STORYTELLING — Problem → Yaklaşım → Sistem ═══ */}
      <section className="py-24 lg:py-32 px-6 lg:px-12" data-testid="project-story">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-20">
            <span className="text-xs font-mono text-violet-400/70 tracking-widest uppercase block mb-3">Proje Hikayesi</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Nasıl Dönüştürdük?</h2>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              { icon: <Target className="w-6 h-6" />, color: 'red', title: 'Problem', text: project.challenge, num: '01' },
              { icon: <Zap className="w-6 h-6" />, color: 'violet', title: 'Yaklaşım', text: project.solution, num: '02' },
              { icon: <TrendingUp className="w-6 h-6" />, color: 'emerald', title: 'Sonuç', text: project.results?.[0] || 'Ölçülebilir büyüme sağlandı', num: '03' },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="group relative p-8 rounded-2xl border border-zinc-800/60 bg-zinc-900/30 hover:border-zinc-700/60 transition-all duration-500 hover:-translate-y-1"
              >
                <span className="text-xs font-mono text-zinc-600 absolute top-6 right-6">{item.num}</span>
                <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/10 flex items-center justify-center text-${item.color}-400 mb-5`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-zinc-400 text-base leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 3. PREMIUM MASONRY GALLERY ═══ */}
      {galleryImages.length > 0 && (
        <section className="py-24 lg:py-32 px-6 lg:px-12" data-testid="project-gallery">
          <div className="max-w-7xl mx-auto">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-14">
              <span className="text-xs font-mono text-violet-400/70 tracking-widest uppercase block mb-3">Proje Detayları</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Görsel Galeri</h2>
              <p className="text-zinc-500 mt-3 text-base">{galleryImages.length} görsel</p>
            </motion.div>
            <MasonryGallery images={galleryImages} projectTitle={project.title} />
          </div>
        </section>
      )}

      {/* ═══ 4. RESULTS DASHBOARD ═══ */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 relative" data-testid="project-results">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] bg-violet-600/[0.03] rounded-full blur-[140px]" />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-14">
            <span className="text-xs font-mono text-emerald-400/70 tracking-widest uppercase block mb-3">Sonuçlar</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Ölçülebilir Başarı</h2>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 mb-10">
            {project.results.map((result, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="p-7 rounded-2xl border border-zinc-800/60 bg-zinc-900/30 text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-white font-semibold text-lg">{result}</p>
              </motion.div>
            ))}
          </div>

          {/* services */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }} className="flex flex-wrap justify-center gap-3 pt-8 border-t border-zinc-800/40">
            <p className="w-full text-center text-xs text-zinc-600 uppercase tracking-widest mb-3">Uygulanan Hizmetler</p>
            {project.services.map((s, i) => (
              <span key={i} className="px-5 py-2.5 rounded-full bg-zinc-900/50 border border-zinc-800/50 text-zinc-300 text-sm font-medium hover:border-violet-500/30 transition-colors">
                {s}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ 5. RELATED PROJECTS — HORIZONTAL CAROUSEL ═══ */}
      <section className="py-24 lg:py-32 border-t border-zinc-800/30 overflow-hidden" data-testid="related-projects">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }} className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs font-mono text-violet-400/70 tracking-widest uppercase block mb-3">Keşfet</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">Diğer Projeler</h2>
            </div>
            <Link to="/portfolyo" className="hidden sm:inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
              Tümünü Gör <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>

        <ProjectCarousel projects={relatedProjects} />

        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-8 sm:hidden">
          <Link to="/portfolyo" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
            Tümünü Gör <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ═══ 6. CTA ═══ */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-violet-600/[0.04] rounded-full blur-[160px]" />
        </div>
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.7 }} className="max-w-2xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Benzer Sonuçlar<br /><span className="text-zinc-500">Sizin İçin de Mümkün</span>
          </h2>
          <p className="text-zinc-400 text-base mb-10 leading-relaxed">
            Markanız için özel büyüme sistemi kurmak ister misiniz?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/buyume-yolculugu" className="group inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-base" style={{ background: 'linear-gradient(135deg, #5558E3 0%, #7C52D9 60%, #D43D85 100%)' }} data-testid="project-cta">
              Büyüme Yolculuğunu Başlat
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/portfolyo" className="px-6 py-3 border border-zinc-700 text-zinc-300 font-medium rounded-full hover:border-zinc-500 hover:text-white transition-colors text-sm">
              Daha Fazla Proje
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

/* ─── HORIZONTAL PROJECT CAROUSEL ─── */
function ProjectCarousel({ projects }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-card]')?.offsetWidth || 400;
    el.scrollBy({ left: dir * (cardWidth + 24), behavior: 'smooth' });
  };

  return (
    <div className="relative group/carousel">
      {/* Arrow buttons */}
      <AnimatePresence>
        {canScrollLeft && (
          <motion.button
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            onClick={() => scroll(-1)}
            className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-zinc-900/80 backdrop-blur-md border border-zinc-700/50 flex items-center justify-center text-white hover:bg-zinc-800 hover:border-zinc-600 transition-all shadow-2xl"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {canScrollRight && (
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            onClick={() => scroll(1)}
            className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-zinc-900/80 backdrop-blur-md border border-zinc-700/50 flex items-center justify-center text-white hover:bg-zinc-800 hover:border-zinc-600 transition-all shadow-2xl"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Fade edges */}
      {canScrollLeft && <div className="absolute left-0 top-0 bottom-0 w-16 lg:w-24 bg-gradient-to-r from-[#070707] to-transparent z-10 pointer-events-none" />}
      {canScrollRight && <div className="absolute right-0 top-0 bottom-0 w-16 lg:w-24 bg-gradient-to-l from-[#070707] to-transparent z-10 pointer-events-none" />}

      {/* Scrollable track */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto px-6 lg:px-12 pb-4 scroll-smooth project-carousel"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        <style>{`
          .project-carousel::-webkit-scrollbar { display: none; }
        `}</style>
        {projects.map((p, i) => (
          <motion.div
            key={p.slug}
            data-card
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className="flex-shrink-0 w-[320px] sm:w-[380px] lg:w-[420px]"
          >
            <Link to={`/proje/${p.slug}`} className="group block">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-4 border border-zinc-800/40 bg-zinc-900">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Metric badge */}
                {p.metric && (
                  <div className="absolute bottom-4 left-4 px-4 py-2 rounded-full bg-black/60 backdrop-blur-sm border border-zinc-700/40 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                    <span className="text-sm font-bold text-emerald-400">{p.metric}</span>
                    <span className="text-xs text-zinc-400 ml-1.5">{p.metricLabel}</span>
                  </div>
                )}

                {/* Category pill top-right */}
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-zinc-700/30 text-xs text-zinc-300 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {p.categoryLabel}
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white group-hover:text-violet-300 transition-colors duration-300">{p.title}</h3>
              <p className="text-sm text-zinc-500 mt-1 line-clamp-1">{p.description}</p>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ─── MASONRY GALLERY WITH LIGHTBOX ─── */
function MasonryGallery({ images, projectTitle }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);
  const goPrev = (e) => { e.stopPropagation(); setLightboxIdx(i => i === 0 ? images.length - 1 : i - 1); };
  const goNext = (e) => { e.stopPropagation(); setLightboxIdx(i => i === images.length - 1 ? 0 : i + 1); };

  // masonry: alternate between tall and normal
  const getSpan = (i) => {
    const pattern = [false, false, true, false, true, false, false, false, true, false];
    return pattern[i % pattern.length];
  };

  return (
    <>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
        {images.map((img, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
            className="group relative rounded-xl overflow-hidden cursor-pointer break-inside-avoid bg-zinc-900"
            onClick={() => openLightbox(i)}
            data-testid={`gallery-image-${i}`}
          >
            <img
              src={img}
              alt={`${projectTitle} - ${i + 1}`}
              className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500 flex items-center justify-center">
              <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center" onClick={closeLightbox}>
            <button className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10" onClick={closeLightbox} data-testid="lightbox-close">
              <X className="w-6 h-6 text-white" />
            </button>
            <button className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10" onClick={goPrev} data-testid="lightbox-prev">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10" onClick={goNext} data-testid="lightbox-next">
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
            <motion.img key={lightboxIdx} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} src={images[lightboxIdx]} alt={`${projectTitle} - ${lightboxIdx + 1}`} className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-5 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-mono">
              {lightboxIdx + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
